import surfaceGLSL from "./utils/surface.glsl";

import normalsGLSL from "./utils/normals.glsl";

const fragmentShader = /* glsl */ `
precision highp float;

varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vWorldPosition;


${surfaceGLSL}
${normalsGLSL}


/*
 * Lighting.
 */
uniform vec3 uLightDirection;
uniform vec3 uLightColor;

uniform float uLightIntensity;
uniform float uAmbientIntensity;


/*
 * Halftone.
 */
uniform vec3 uInkColor;

uniform float uDotScale;
uniform float uDotAngle;
uniform float uSoftness;


/*
 * ============================================================
 * HALFTONE PATTERN
 * ============================================================
 */

vec2 rotatePattern(
    vec2 point,
    float angle
) {

    float cosine =
        cos(
            angle
        );

    float sine =
        sin(
            angle
        );


    return mat2(
        cosine,
        -sine,
        sine,
        cosine
    ) *
    point;
}


float getHalftone(
    float darkness
) {

    /*
     * Screen-space coordinates mean
     * the pattern remains attached to
     * the image rather than the UV map.
     */
    vec2 point =
        gl_FragCoord.xy /
        max(
            uDotScale,
            1.0
        );


    point =
        rotatePattern(
            point,
            uDotAngle
        );


    /*
     * Position inside the current
     * halftone cell.
     */
    vec2 cell =
        fract(
            point
        ) -
        0.5;


    float distanceFromCenter =
        length(
            cell
        );


    /*
     * More darkness produces larger
     * ink dots.
     *
     * Maximum radius is slightly larger
     * than half a cell so neighboring
     * dots can merge in deep shadow.
     */
    float radius =
        darkness *
        0.72;


    /*
     * Anti-aliased transition.
     */
    float edgeWidth =
        max(
            fwidth(
                distanceFromCenter
            ),
            0.001
        ) +
        uSoftness *
        0.1;


    return 1.0 -
        smoothstep(
            radius -
            edgeWidth,
            radius +
            edgeWidth,
            distanceFromCenter
        );
}


/*
 * ============================================================
 * MAIN
 * ============================================================
 */

void main() {

    vec2 surfaceUv =
        transformSurfaceUv(
            vUv
        );


    vec3 surfaceColor =
        getSurfaceColor(
            surfaceUv
        );


    float ao =
        getAmbientOcclusion(
            surfaceUv
        );


    vec3 emissive =
        getEmissiveColor(
            surfaceUv
        );


    vec3 surfaceNormal =
        getSurfaceNormal(
            surfaceUv
        );


    vec3 lightDirection =
        normalize(
            uLightDirection
        );


    float diffuse =
        max(
            dot(
                surfaceNormal,
                lightDirection
            ),
            0.0
        );


    float ambient =
        uAmbientIntensity *
        ao;


    float illumination =
        clamp(
            ambient +
            diffuse *
            uLightIntensity,
            0.0,
            1.0
        );


    float darkness =
        1.0 -
        illumination;


    float ink =
        getHalftone(
            darkness
        );


    /*
     * Conventional diffuse surface
     * underneath the halftone pattern.
     */
    vec3 lighting =
        vec3(
            ambient
        ) +
        (
            uLightColor *
            diffuse *
            uLightIntensity
        );


    lighting =
        clamp(
            lighting,
            0.0,
            1.0
        );


    vec3 litSurface =
        surfaceColor *
        lighting;


    /*
     * ink = 0 -> normal surface
     * ink = 1 -> ink color
     */
    vec3 halftoneColor =
        mix(
            litSurface,
            uInkColor,
            ink
        );


    vec3 finalColor =
        halftoneColor +
        emissive;


    gl_FragColor =
        vec4(
            finalColor,
            1.0
        );
}

`;

export default fragmentShader;
